/**
 * CreateProjectPage - create or edit a project
 * Routes: /projects/new (create), /projects/:projectId/edit (edit)
 */
import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { ConstraintsInput } from '@/components/forms/ConstraintsInput'
import { ProjectKnowledgeBaseEditor } from '@/components/projects'
import {
  useProject,
  useCreateProject,
  useUpdateProject
} from '@/api/projects'
import { usePermissions } from '@/hooks/usePermissions'
import type { ProjectContextItemInput } from '@/lib/types'

interface FormData {
  name: string
  description: string
  constraints: string[]
  contextTemplate: string
  defaultRepository: string
  defaultBranch: string
  contextItems: ProjectContextItemInput[]
}

interface FormErrors {
  name?: string
  description?: string
  defaultRepository?: string
}

const initialFormData: FormData = {
  name: '',
  description: '',
  constraints: [],
  contextTemplate: '',
  defaultRepository: '',
  defaultBranch: '',
  contextItems: []
}

/**
 * Page for creating a new project or editing an existing one.
 * Validates form inputs and handles API submission.
 */
export function CreateProjectPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const isEditMode = !!projectId

  // Permission checks
  const { hasPermission, isLoading: permissionsLoading } = usePermissions()
  const canCreateProject = hasPermission('project:create')
  const canEditProject = hasPermission('project:update')

  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Fetch existing project for edit mode
  const {
    data: existingProject,
    isLoading: projectLoading
  } = useProject(isEditMode ? projectId : undefined)

  // Mutations
  const createProject = useCreateProject()
  const updateProject = useUpdateProject()

  // Check permissions - redirect if not allowed
  if (!permissionsLoading) {
    if (isEditMode && !canEditProject) {
      return <Navigate to="/projects" replace />
    }
    if (!isEditMode && !canCreateProject) {
      return <Navigate to="/projects" replace />
    }
  }

  // Populate form with existing project data
  useEffect(() => {
    if (existingProject) {
      setFormData({
        name: existingProject.name,
        description: existingProject.description ?? '',
        constraints: existingProject.constraints ?? [],
        contextTemplate: existingProject.contextTemplate ?? '',
        defaultRepository: existingProject.defaultRepository ?? '',
        defaultBranch: existingProject.defaultBranch ?? '',
        contextItems: existingProject.contextItems?.map(item => ({
          type: item.type,
          value: item.value,
          note: item.note
        })) ?? []
      })
    }
  }, [existingProject])

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less'
    } else if (!/^[a-z0-9-]+$/.test(formData.name)) {
      newErrors.name = 'Name must be in kebab-case (lowercase letters, numbers, and hyphens only)'
    }

    if (formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less'
    }

    if (formData.defaultRepository && !isValidUrl(formData.defaultRepository)) {
      newErrors.defaultRepository = 'Please enter a valid URL'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    if (!validateForm()) {
      return
    }

    try {
      if (isEditMode && projectId) {
        await updateProject.mutateAsync({
          projectId,
          request: formData
        })
        navigate(`/projects/${projectId}`)
      } else {
        const result = await createProject.mutateAsync(formData)
        navigate(`/projects/${result.id}`)
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? 'update' : 'create'} project`
      )
    }
  }

  const handleCancel = () => {
    if (isEditMode && projectId) {
      navigate(`/projects/${projectId}`)
    } else {
      navigate('/projects')
    }
  }

  const updateField = <K extends keyof FormData>(
    field: K,
    value: FormData[K]
  ) => {
    // Transform name field to kebab-case
    if (field === 'name' && typeof value === 'string') {
      value = value.toLowerCase().replace(/[^a-z0-9-]/g, '-') as FormData[K]
    }
    
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when field is modified
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const isPending = createProject.isPending || updateProject.isPending

  // Loading state for edit mode
  if (isEditMode && projectLoading) {
    return (
      <PageShell>
        <div className="p-6 space-y-6" data-testid="form-skeleton">
          <div className="space-y-6">
            <Skeleton className="h-8 w-48" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Skeleton className="h-64 w-full" />
              </div>
              <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Header with Actions */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2">
              <Link to="/projects">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Projects
              </Link>
            </Button>
            <h1 className="text-2xl font-bold">
              {isEditMode ? 'Edit Project' : 'Create Project'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEditMode
                ? 'Update your project settings and constraints.'
                : 'Set up a new project with reusable constraints and context.'}
            </p>
          </div>
          <div className="flex items-center gap-3 pt-8">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" form="project-form" disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isPending
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? 'Save Changes' : 'Create Project')}
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {submitError && (
          <Alert variant="destructive" className="mb-6">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        <form id="project-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Two-column layout: Metadata (1/3) + Context (2/3) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Metadata */}
            <div className="space-y-6">
              {/* Basic Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      placeholder="my-project"
                      aria-describedby={errors.name ? 'name-error' : undefined}
                      aria-invalid={!!errors.name}
                    />
                    {errors.name && (
                      <p id="name-error" className="text-sm text-destructive">
                        {errors.name}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Use lowercase letters, numbers, and hyphens only
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => updateField('description', e.target.value)}
                      placeholder="A brief description of your project..."
                      rows={3}
                      aria-describedby={errors.description ? 'description-error' : undefined}
                      aria-invalid={!!errors.description}
                    />
                    {errors.description && (
                      <p id="description-error" className="text-sm text-destructive">
                        {errors.description}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {formData.description.length}/500 characters
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Repository */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Repository</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="defaultRepository">Repository URL</Label>
                    <Input
                      id="defaultRepository"
                      type="url"
                      value={formData.defaultRepository}
                      onChange={(e) => updateField('defaultRepository', e.target.value)}
                      placeholder="https://github.com/org/repo"
                      aria-describedby={errors.defaultRepository ? 'repository-error' : undefined}
                      aria-invalid={!!errors.defaultRepository}
                    />
                    {errors.defaultRepository && (
                      <p id="repository-error" className="text-sm text-destructive">
                        {errors.defaultRepository}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="defaultBranch">Default Branch</Label>
                    <Input
                      id="defaultBranch"
                      value={formData.defaultBranch}
                      onChange={(e) => updateField('defaultBranch', e.target.value)}
                      placeholder="main"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column: Context (Primary) */}
            <div className="lg:col-span-2">
              <Card className="h-full flex flex-col">
                <CardHeader>
                  <CardTitle>Project Context</CardTitle>
                  <CardDescription>
                    Additional context that will be provided to all sessions.
                    This can include architectural decisions, conventions, coding standards, etc.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <Textarea
                    id="contextTemplate"
                    value={formData.contextTemplate}
                    onChange={(e) => updateField('contextTemplate', e.target.value)}
                    placeholder="Enter project context, architectural decisions, or other relevant information that should be available to all sessions...

Examples:
• This is a React/TypeScript project using shadcn/ui components
• Follow the existing patterns in src/components
• All API calls should go through the hooks in src/api
• Use TanStack Query for data fetching"
                    className="flex-1 w-full min-h-[280px] resize-y"
                    aria-label="Context"
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Constraints - Full Width */}
          <Card>
            <CardHeader>
              <CardTitle>Constraints</CardTitle>
              <CardDescription>
                Define rules that will be applied to all sessions in this project.
                Use structured constraints like <code className="bg-muted px-1 rounded text-xs">scope:path:Only modify src/**</code> for better agent targeting.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ConstraintsInput
                value={formData.constraints}
                onChange={(constraints) => updateField('constraints', constraints)}
                maxConstraints={20}
                defaultMode="simple"
                showModeToggle={true}
                showTemplates={true}
              />
            </CardContent>
          </Card>

          {/* Knowledge Base - Full Width */}
          <Card>
            <CardHeader>
              <CardTitle>Knowledge Base</CardTitle>
              <CardDescription>
                Reference ADRs, architecture docs, epics, and other resources that provide context 
                for all sessions. These items will be automatically included in the AI&apos;s context.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ProjectKnowledgeBaseEditor
                items={formData.contextItems}
                onChange={(contextItems) => updateField('contextItems', contextItems)}
                maxItems={50}
              />
            </CardContent>
          </Card>
        </form>
      </div>
    </PageShell>
  )
}
